/**
 * Cascade SaaS – Shared UI Components
 */

import {useCallback, useEffect, useRef, useState, type ReactNode} from 'react';
import {useAuth} from '../auth';
import './shared.scss';

/* ── SVG Icons ── */

export function CascadeLogomark({size = 15}: {size?: number}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3L20 7.5V16.5L12 21L4 16.5V7.5L12 3Z" />
      <path d="M12 12L20 7.5" />
      <path d="M12 12V21" />
      <path d="M12 12L4 7.5" />
    </svg>
  );
}

function LogOutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/* ── Navbar ── */

export function Navbar({children}: {children?: ReactNode}) {
  return (
    <nav className="navbar">
      {children}
    </nav>
  );
}

Navbar.Left = function NavbarLeft({children}: {children?: ReactNode}) {
  return <div className="navbar__left">{children}</div>;
};

Navbar.Right = function NavbarRight({children}: {children?: ReactNode}) {
  return <div className="navbar__right">{children}</div>;
};

Navbar.Logo = function NavbarLogo({href = '#/'}: {href?: string}) {
  return (
    <a className="navbar__logo" href={href}>
      <div className="navbar__logomark">
        <CascadeLogomark />
      </div>
      <span>Cascade</span>
    </a>
  );
};

Navbar.Breadcrumb = function NavbarBreadcrumb({label}: {label: string}) {
  return (
    <div className="navbar__breadcrumb">
      <span className="navbar__breadcrumb-sep">/</span>
      <span className="navbar__breadcrumb-current">{label}</span>
    </div>
  );
};

Navbar.Badge = function NavbarBadge({children}: {children: ReactNode}) {
  return <span className="navbar__badge">{children}</span>;
};

/* ── User Menu ── */

export function UserMenu() {
  const {user, signOut} = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const initial = (user?.email ?? '?')[0].toUpperCase();

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, handleClickOutside]);

  return (
    <div className="user-menu" ref={ref}>
      <button
        className="user-menu__trigger"
        onClick={() => setOpen(o => !o)}
        title={user?.email ?? ''}
      >
        {initial}
      </button>
      {open && (
        <div className="user-menu__dropdown">
          <div className="user-menu__email">{user?.email}</div>
          <button className="user-menu__item" onClick={signOut}>
            <LogOutIcon />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Modal ── */

export function Modal({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

Modal.Title = function ModalTitle({children}: {children: ReactNode}) {
  return <h2 className="modal__title">{children}</h2>;
};

Modal.Description = function ModalDescription({children}: {children: ReactNode}) {
  return <p className="modal__description">{children}</p>;
};

Modal.Actions = function ModalActions({children}: {children: ReactNode}) {
  return <div className="modal__actions">{children}</div>;
};

/* ── Spinner ── */

export function Spinner({size = 'md'}: {size?: 'sm' | 'md' | 'lg'}) {
  return <div className={`spinner spinner--${size}`} />;
}

/* ── Page Loader ── */

export function PageLoader({text}: {text?: string}) {
  return (
    <div className="page-loader">
      <Spinner size="lg" />
      {text && <span className="page-loader__text">{text}</span>}
    </div>
  );
}

/* ── Empty State ── */

export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon?: ReactNode;
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-state__icon">{icon}</div>}
      <h3 className="empty-state__title">{title}</h3>
      {text && <p className="empty-state__text">{text}</p>}
      {action}
    </div>
  );
}
